import { useEffect, useMemo, useState } from "react";
import {
  filterManufacturingModels,
  type ModelsActiveFilter,
} from "@/lib/manufacturing/filterManufacturingModels";
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";
import {
  listManufacturingRecipeGroups,
  type MfgRecipeGroupSummary,
} from "@/lib/manufacturing/recipeGroupsClient";
import { useManufacturingModelsListPagination } from "./useManufacturingModelsListPagination";

export function useManufacturingModelsListView(recipes: readonly MfgRecipeSummary[]) {
  const [search, setSearchState] = useState("");
  const [activeFilter, setActiveFilterState] = useState<ModelsActiveFilter>("active");
  const [needsReviewOnly, setNeedsReviewOnlyState] = useState(false);
  const [recipeGroupId, setRecipeGroupIdState] = useState("");
  const [recipeGroups, setRecipeGroups] = useState<MfgRecipeGroupSummary[]>([]);

  function reloadRecipeGroups() {
    void listManufacturingRecipeGroups().then((r) => {
      if (r.ok) setRecipeGroups(r.data.groups);
    });
  }

  useEffect(() => {
    reloadRecipeGroups();
  }, []);

  const filtered = useMemo(
    () =>
      filterManufacturingModels(recipes, {
        search,
        activeFilter,
        needsReviewOnly,
        recipeGroupId,
      }),
    [recipes, search, activeFilter, needsReviewOnly, recipeGroupId],
  );
  const pager = useManufacturingModelsListPagination(filtered);

  function setSearch(next: string) {
    setSearchState(next);
    pager.setPage(0);
  }

  function setActiveFilter(next: ModelsActiveFilter) {
    setActiveFilterState(next);
    pager.setPage(0);
  }

  function setNeedsReviewOnly(next: boolean) {
    setNeedsReviewOnlyState(next);
    pager.setPage(0);
  }

  function setRecipeGroupId(next: string) {
    setRecipeGroupIdState(next);
    pager.setPage(0);
  }

  return {
    search,
    setSearch,
    activeFilter,
    setActiveFilter,
    needsReviewOnly,
    setNeedsReviewOnly,
    recipeGroupId,
    setRecipeGroupId,
    recipeGroups,
    reloadRecipeGroups,
    filtered,
    filteredCount: filtered.length,
    page: pager.page,
    pageSize: pager.pageSize,
    pageCount: pager.pageCount,
    rangeStart: pager.rangeStart,
    rangeEnd: pager.rangeEnd,
    pageItems: pager.pageItems,
    setPage: pager.setPage,
    setPageSize: pager.setPageSize,
  };
}

export type { ModelsActiveFilter };
